import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaUserGraduate, 
  FaSearch, 
  FaFilter, 
  FaPlus, 
  FaEye, 
  FaEdit, 
  FaTrash,
  FaFileExport,
  FaSortAmountDown,
  FaSortAmountUp,
  FaChevronLeft,
  FaChevronRight,
  FaEnvelope,
  FaCheckCircle,
  FaExclamationCircle
} from 'react-icons/fa';

const StudentList = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [programFilter, setProgramFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [studentsPerPage] = useState(10);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Mock data for demonstration
  const mockStudents = [
    {
      _id: 'stu001',
      name: 'John Doe',
      email: 'john.doe@example.com',
      studentId: 'STU123456',
      program: 'Computer Science',
      level: 'Undergraduate',
      enrollmentDate: '2023-09-01',
      status: 'Active',
      progress: 65,
      coursesEnrolled: 4,
      gpa: 3.75
    },
    {
      _id: 'stu002',
      name: 'Jane Smith',
      email: 'jane.smith@example.com',
      studentId: 'STU789012',
      program: 'Business Administration',
      level: 'Undergraduate',
      enrollmentDate: '2023-08-15',
      status: 'Active',
      progress: 72,
      coursesEnrolled: 5,
      gpa: 3.9
    },
    {
      _id: 'stu003',
      name: 'Michael Johnson',
      email: 'michael.johnson@example.com',
      studentId: 'STU345678',
      program: 'Computer Science',
      level: 'Graduate',
      enrollmentDate: '2023-09-10',
      status: 'Active',
      progress: 45,
      coursesEnrolled: 3,
      gpa: 3.5
    },
    {
      _id: 'stu004',
      name: 'Emily Davis',
      email: 'emily.davis@example.com',
      studentId: 'STU901234',
      program: 'Data Science',
      level: 'Graduate',
      enrollmentDate: '2023-07-20',
      status: 'Inactive',
      progress: 0,
      coursesEnrolled: 0,
      gpa: 0
    },
    {
      _id: 'stu005',
      name: 'Robert Wilson',
      email: 'robert.wilson@example.com',
      studentId: 'STU567890',
      program: 'Artificial Intelligence',
      level: 'Graduate',
      enrollmentDate: '2023-08-05',
      status: 'Active',
      progress: 80,
      coursesEnrolled: 2,
      gpa: 4.0
    },
    {
      _id: 'stu006',
      name: 'Sarah Brown',
      email: 'sarah.brown@example.com',
      studentId: 'STU234567',
      program: 'Business Administration',
      level: 'Undergraduate',
      enrollmentDate: '2023-09-05',
      status: 'Active',
      progress: 60,
      coursesEnrolled: 5,
      gpa: 3.2
    },
    {
      _id: 'stu007',
      name: 'David Miller',
      email: 'david.miller@example.com',
      studentId: 'STU890123',
      program: 'Cybersecurity',
      level: 'Undergraduate',
      enrollmentDate: '2023-08-25',
      status: 'Active',
      progress: 55,
      coursesEnrolled: 4,
      gpa: 3.4
    },
    {
      _id: 'stu008',
      name: 'Jennifer Taylor',
      email: 'jennifer.taylor@example.com',
      studentId: 'STU456789',
      program: 'Data Science',
      level: 'Graduate',
      enrollmentDate: '2023-07-15',
      status: 'On Leave',
      progress: 30,
      coursesEnrolled: 2,
      gpa: 3.6
    },
    {
      _id: 'stu009',
      name: 'Thomas Anderson',
      email: 'thomas.anderson@example.com',
      studentId: 'STU012345',
      program: 'Artificial Intelligence',
      level: 'Graduate',
      enrollmentDate: '2023-09-15',
      status: 'Active',
      progress: 25,
      coursesEnrolled: 3,
      gpa: 3.8
    },
    {
      _id: 'stu010',
      name: 'Lisa Martinez',
      email: 'lisa.martinez@example.com',
      studentId: 'STU678901',
      program: 'Computer Science',
      level: 'Undergraduate',
      enrollmentDate: '2023-08-10',
      status: 'Active',
      progress: 70,
      coursesEnrolled: 5,
      gpa: 3.7
    },
    {
      _id: 'stu011',
      name: 'James Wilson',
      email: 'james.wilson@example.com',
      studentId: 'STU345678',
      program: 'Business Administration',
      level: 'Undergraduate',
      enrollmentDate: '2023-07-25',
      status: 'Active',
      progress: 85,
      coursesEnrolled: 4,
      gpa: 3.9
    },
    {
      _id: 'stu012',
      name: 'Patricia Moore',
      email: 'patricia.moore@example.com',
      studentId: 'STU901234',
      program: 'Cybersecurity',
      level: 'Graduate',
      enrollmentDate: '2023-09-20',
      status: 'Active',
      progress: 40,
      coursesEnrolled: 3,
      gpa: 3.5
    }
  ];

  // Available programs for filtering
  const programs = [
    'All Programs',
    'Computer Science',
    'Business Administration',
    'Data Science',
    'Artificial Intelligence',
    'Cybersecurity'
  ];

  useEffect(() => {
    // Simulate API call
    const fetchStudents = async () => {
      setLoading(true);
      try {
        // In a real app, this would be an API call
        // const response = await fetch('/api/students');
        // const data = await response.json();
        
        // Using mock data for demonstration
        setTimeout(() => {
          setStudents(mockStudents);
          setFilteredStudents(mockStudents);
          setLoading(false);
        }, 800);
      } catch (error) {
        console.error('Error fetching students:', error);
        setLoading(false);
      }
    };

    fetchStudents();
  }, []);

  useEffect(() => {
    // Apply filters and search
    let result = [...students];
    
    // Apply search term
    if (searchTerm) {
      result = result.filter(student => 
        student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.studentId.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(student => student.status === statusFilter);
    }
    
    // Apply program filter
    if (programFilter !== 'all') {
      result = result.filter(student => student.program === programFilter);
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Handle string comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    setFilteredStudents(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [students, searchTerm, statusFilter, programFilter, sortField, sortDirection]);

  // Handle sort toggle
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle checkbox selection
  const handleSelectStudent = (studentId) => {
    if (selectedStudents.includes(studentId)) {
      setSelectedStudents(selectedStudents.filter(id => id !== studentId));
    } else {
      setSelectedStudents([...selectedStudents, studentId]);
    }
  };

  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(currentStudents.map(student => student._id));
    }
    setSelectAll(!selectAll);
  };

  // Pagination logic
  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(indexOfFirstStudent, indexOfLastStudent);
  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Student Management</h1>
        <div className="flex gap-2">
          <button className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark flex items-center">
            <FaPlus className="mr-2" />
            Add Student
          </button>
          <button className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 flex items-center">
            <FaFileExport className="mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <h2 className="font-bold">Filters and Search</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Search by name, email, ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="On Leave">On Leave</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Program</label>
              <select
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={programFilter}
                onChange={(e) => setProgramFilter(e.target.value)}
              >
                <option value="all">All Programs</option>
                {programs.slice(1).map((program, index) => (
                  <option key={index} value={program}>{program}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button 
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center justify-center"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setProgramFilter('all');
                }}
              >
                <FaFilter className="mr-2" />
                Reset Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Students Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="font-bold">Students ({filteredStudents.length})</h2>
          <div className="text-sm text-gray-500">
            {selectedStudents.length > 0 ? (
              <div className="flex items-center">
                <span className="mr-2">{selectedStudents.length} selected</span>
                <button className="text-red-600 hover:text-red-800 mr-3">
                  <FaTrash />
                </button>
                <button className="text-primary hover:text-primary-dark mr-3">
                  <FaEnvelope />
                </button>
              </div>
            ) : (
              <span>Select students to perform actions</span>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center">
                    <span>Student</span>
                    {sortField === 'name' && (
                      sortDirection === 'asc' ? 
                        <FaSortAmountUp className="ml-1" /> : 
                        <FaSortAmountDown className="ml-1" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('program')}
                >
                  <div className="flex items-center">
                    <span>Program</span>
                    {sortField === 'program' && (
                      sortDirection === 'asc' ? 
                        <FaSortAmountUp className="ml-1" /> : 
                        <FaSortAmountDown className="ml-1" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('enrollmentDate')}
                >
                  <div className="flex items-center">
                    <span>Enrollment Date</span>
                    {sortField === 'enrollmentDate' && (
                      sortDirection === 'asc' ? 
                        <FaSortAmountUp className="ml-1" /> : 
                        <FaSortAmountDown className="ml-1" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center">
                    <span>Status</span>
                    {sortField === 'status' && (
                      sortDirection === 'asc' ? 
                        <FaSortAmountUp className="ml-1" /> : 
                        <FaSortAmountDown className="ml-1" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('progress')}
                >
                  <div className="flex items-center">
                    <span>Progress</span>
                    {sortField === 'progress' && (
                      sortDirection === 'asc' ? 
                        <FaSortAmountUp className="ml-1" /> : 
                        <FaSortAmountDown className="ml-1" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('gpa')}
                >
                  <div className="flex items-center">
                    <span>GPA</span>
                    {sortField === 'gpa' && (
                      sortDirection === 'asc' ? 
                        <FaSortAmountUp className="ml-1" /> : 
                        <FaSortAmountDown className="ml-1" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentStudents.map((student) => (
                <tr key={student._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      checked={selectedStudents.includes(student._id)}
                      onChange={() => handleSelectStudent(student._id)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <FaUserGraduate className="text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{student.name}</div>
                        <div className="text-sm text-gray-500">{student.email}</div>
                        <div className="text-xs text-gray-400">{student.studentId}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{student.program}</div>
                    <div className="text-sm text-gray-500">{student.level}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(student.enrollmentDate)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      student.status === 'Active' ? 'bg-green-100 text-green-800' : 
                      student.status === 'Inactive' ? 'bg-red-100 text-red-800' : 
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {student.status === 'Active' ? <FaCheckCircle className="mr-1" /> : 
                       student.status === 'Inactive' ? <FaExclamationCircle className="mr-1" /> : 
                       <FaExclamationCircle className="mr-1" />}
                      {student.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1">
                      <div 
                        className={`h-2.5 rounded-full ${
                          student.progress >= 70 ? 'bg-green-500' : 
                          student.progress >= 40 ? 'bg-yellow-500' : 
                          'bg-red-500'
                        }`}
                        style={{ width: `${student.progress}%` }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-500 text-right">{student.progress}%</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium">{student.gpa.toFixed(2)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <Link to={`/dashboard/students/${student._id}`} className="text-primary hover:text-primary-dark">
                        <FaEye />
                      </Link>
                      <Link to={`/dashboard/students/${student._id}/edit`} className="text-blue-600 hover:text-blue-800">
                        <FaEdit />
                      </Link>
                      <button className="text-red-600 hover:text-red-800">
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 flex items-center justify-between border-t">
            <div className="text-sm text-gray-500">
              Showing {indexOfFirstStudent + 1} to {Math.min(indexOfLastStudent, filteredStudents.length)} of {filteredStudents.length} students
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                <FaChevronLeft />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                <button
                  key={number}
                  onClick={() => paginate(number)}
                  className={`px-3 py-1 rounded ${currentPage === number ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  {number}
                </button>
              ))}
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentList;